import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  TabPanel,
  TabList,
} from '@mui/joy';
import { ArticleImages } from '@/components/Pages/Articles/ArticleImages';
import { ArticleResponseDTO, QueryResponse } from '@/types';
import { ArticleStockModal } from '@/components/Pages/Articles/ArticleStockModal';
import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { CreateEditArticleModal } from '@/components/Pages/Articles/CreateEditArticleModal';
import { createFileRoute } from '@tanstack/react-router';
import { DeleteArticleModal } from '@/components/Pages/Articles/DeleteArticleModal';
import { Link } from '@tanstack/react-router';
import { StockMovementsTable } from '@/components/Pages/Articles/StockMovementsTable';
import { Table, createColumn } from '@/components/UI/Table';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/axios';

export const Route = createFileRoute('/_protected/articles/$id')({
  component: ArticleDetail,
});

function ArticleDetail() {
  const { id } = Route.useParams();
  const { isAdmin, isSuperAdmin } = useAuth();
  const isAdminRole = isAdmin || isSuperAdmin;

  const { data: article, isLoading } = useQuery<
    QueryResponse<ArticleResponseDTO>,
    AxiosError,
    ArticleResponseDTO
  >({
    queryKey: ['articles', id],
    queryFn: () => api.get(`/article/${id}`),
    select: ({ data }) => data.data,
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return <div>Artículo no encontrado</div>;
  }

  return (
    <>
      <Stack direction='row' justifyContent='space-between' sx={{ mb: 2 }}>
        <Breadcrumbs size='sm'>
          <Link to='/articles'>Artículos</Link>
          <Typography>{article?.name}</Typography>
        </Breadcrumbs>

        {isAdminRole && (
          <Stack direction='row' gap={1}>
            <CreateEditArticleModal article={article} />
            <DeleteArticleModal
              id={article?.id.toString()}
              name={article?.name}
            />

            {isSuperAdmin && <ArticleStockModal articleId={article.id} />}
          </Stack>
        )}
      </Stack>

      <Tabs>
        <TabList tabFlex={1}>
          <Tab variant='outlined' color='neutral'>
            Atributos
          </Tab>

          <Tab variant='outlined' color='neutral'>
            Stock
          </Tab>

          <Tab variant='outlined' color='neutral'>
            Imágenes
          </Tab>
        </TabList>

        <TabPanel value={0}>
          <Card>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid xs={12} md={6}>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={article?.name}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>SKU</FormLabel>
                <Input
                  value={article?.sku}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>Categoría</FormLabel>
                <Input
                  value={article?.categoryName}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              {isAdminRole && (
                <>
                  <Grid xs={12} md={6}>
                    <FormLabel>Precio Compra</FormLabel>
                    <Input
                      value={article?.priceBuy ?? 0}
                      startDecorator={<AttachMoneyIcon />}
                      readOnly
                      variant='soft'
                      size='sm'
                      sx={{ backgroundColor: 'background.body' }}
                    />
                  </Grid>
                </>
              )}
              <Grid xs={12} md={6}>
                <FormLabel>Precio Venta</FormLabel>
                <Input
                  value={article?.priceSell ?? 0}
                  startDecorator={<AttachMoneyIcon />}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>Punto de Reaprovisionamiento</FormLabel>
                <Input
                  value={article?.replenishmentPoint ?? 0}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>Disponible para venta directa</FormLabel>
                <Input
                  value={article?.availableDirectSale ? 'Si' : 'No'}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>Mostrar precio</FormLabel>
                <Input
                  value={article?.showPrice ? 'Si' : 'No'}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12}>
                <FormLabel>Descripción</FormLabel>

                <Box sx={{ backgroundColor: 'background.body', p: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: article?.description || '',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>
        </TabPanel>

        <TabPanel value={1}>
          <Card>
            <Box sx={{ mb: 2 }}>
              <Typography level='h4'>Stock</Typography>
            </Box>

            <Table
              data={article.warehouseStock || []}
              columns={[
                createColumn('warehouseName', 'Almacén', 'warehouseName'),
                createColumn('stock', 'Stock', 'stock'),
              ]}
              stripe='odd'
            />
          </Card>

          <StockMovementsTable id={id} />
        </TabPanel>

        <TabPanel value={2}>
          <ArticleImages article={article} />
        </TabPanel>
      </Tabs>
    </>
  );
}
